import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { getBillbyId } from '../api/Api'; // Adjust path as necessary
import logo from "../Assets/Kalam Kids Logo_newlogo.png"; // Ensure you have the logo imported

const numberToWords = (num) => {
    const ones = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
        'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const tens = [
        '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
    ];
    const thousands = ['', 'Thousand', 'Million', 'Billion'];

    if (num === 0) return 'Zero';

    let words = '';
    let i = 0;

    while (num > 0) {
        const chunk = num % 1000;
        if (chunk) {
            let chunkWords = '';
            if (chunk % 100 < 20) {
                chunkWords = ones[chunk % 100];
            } else {
                chunkWords = tens[Math.floor((chunk % 100) / 10)] + (chunk % 10 ? ' ' + ones[chunk % 10] : '');
            }
            if (Math.floor(chunk / 100) > 0) {
                chunkWords = ones[Math.floor(chunk / 100)] + ' Hundred' + (chunkWords ? ' ' + chunkWords : '');
            }
            words = chunkWords + ' ' + thousands[i] + (words ? ' ' + words : '');
        }
        num = Math.floor(num / 1000);
        i++;
    }

    return words.trim();
};

const DownloadBill = () => {
    const { formId, id } = useParams();
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBillData = async () => {
            try {
                const response = await getBillbyId(formId, id);
                if (response.success) {
                    setBill(response.data);
                } else {
                    setError(response.message);
                }
            } catch (err) {
                setError("Failed to fetch bill details for download.");
                console.error("Error fetching bill for download:", err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchBillData();
        }
    }, [id]);

    useEffect(() => {
        if (!loading && bill) {
            window.print();
        }
    }, [loading, bill]);


    if (loading) return <p className="text-center mt-10">Loading bill for download...</p>;
    if (error) return <p className="text-center mt-10 text-red-500">Error: {error}</p>;
    if (!bill) return <p className="text-center mt-10 text-gray-500">No bill data available for download.</p>;

    const totalAmount = bill.amount; // Assuming bill.amount is the total paid for this specific payment
    const totalAmountInWords = numberToWords(totalAmount);

    return (
        <div className="max-w-3xl mx-auto border border-gray-400 p-4 text-[14px] text-gray-800 font-poppins" id="download-bill">
            <style>
                {`
                @media print {
                    body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    font-family: 'Poppins', sans-serif;
                    }
                    table, th, td {
                    border: 1px solid #000;
                    border-collapse: collapse;
                    }
                }
                `}
            </style>

            <div className="text-[#16bab1] text-center mb-4">
                <p className="text-lg font-semibold mt-1 text-gray-600">PAYMENT RECEIPT</p>
            </div>

            <div className="flex justify-between mb-4">
                <div>
                    <img src={logo} alt="logo" className="w-32" />
                </div>
                <div className="flex flex-col space-y-1">
                    <div className="flex space-x-1 items-center">
                        <label className="bg-[#16bab1] text-white w-32 px-2 py-1">Payment ID:</label>
                        <span className="bg-gray-200 px-2 py-1 w-32 flex-1">{bill._id}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <label className="bg-[#16bab1] text-white w-32 px-2 py-1">Date:</label>
                        <span className="bg-gray-200 px-2 py-1 w-36 flex-1">{bill.date?.slice(0, 10).split("-").reverse().join("-")}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                    {[
                        { label: "Received With:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.cashReceivedFrom}</p> },
                        { label: "Relationship:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.relationshipName}</p> },
                        { label: "Admission No:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.registerNo}</p> },
                        { label: "Student Name:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.fullName}</p> },
                        { label: "Class:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.class}</p> },
                        { label: "Academic Year:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.academicYear || "N/A"}</p> },
                    ].map(({ label, element }, i) => (
                        <div key={i} className="flex space-x-1 items-center mb-2">
                            <label className="bg-[#16bab1] text-white text-nowrap w-36 px-2 py-1">{label}</label>
                            {element}
                        </div>
                    ))}
                </div>

                <div>
                    <div className="text-center">
                        {[
                            { label: "Payment Mode:", element: <p className="bg-gray-200 px-2 py-1 flex-1">{bill.paidBy}</p> },
                            { label: "Paid Amount:", element: <p className="bg-gray-200 px-2 py-1 flex-1">₹{bill.amount}</p> },
                            { label: "Remaining Amount:", element: <p className="bg-gray-200 px-2 py-1 flex-1">₹{bill.remaining}</p> },
                        ].map(({ label, element }, i) => (
                            <div key={i} className="flex space-x-1 items-center mb-2">
                                <label className="bg-[#16bab1] text-white text-nowrap w-36 px-2 py-1">{label}</label>
                                {element}
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col space-y-2 mb-2">
                        <label className="bg-[#16bab1] text-white w-full text-center px-2 py-1">Rs. (in words):</label>
                        <input
                            type="text"
                            className="bg-gray-200 outline-none capitalize text-center w-full px-2 py-1"
                            placeholder="(In Words)"
                            value={bill.amountInWords}
                            readOnly
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end space-x-1 mb-2">
                <label className="bg-[#16bab1] text-white w-36 px-2 py-1">Receiver Name:</label>
                <input
                    type="text"
                    className="bg-gray-200 outline-none w-[29%] px-2 py-1"
                    placeholder="(In Words)"
                    value={bill.receiverName}
                    readOnly
                />
            </div>

            <div className="flex justify-between items-center mt-4">
                <p className="text-left font-medium text-sm flex space-x-2 text-[#16bab1]">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                    </svg>
                    <span className=' pt-1'>  www.kalamkids.in </span><br />
                </p>
            </div>
        </div>
    );
};

export default DownloadBill;
